import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// py-components
import { ButtonLoading, ButtonStandard, ModalBase, Text, TextEditor } from "@performyard/py-components";

// hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

// types
import type { Goal } from "../../../../../../api/services";

// utils
import { parseGoalPermissionsForUser } from "../../../utils";

// config
import Config from "../../../config";

type CompleteGoalModalProps = {
	goalId?: string;
	isOpen?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
};

const CompleteGoalModal = ({
	goalId,
	isOpen: isOpenProp,
	onClose,
	onSuccess,
}: CompleteGoalModalProps): React.ReactElement => {
	const { goal_id: goalIdFromParams } = useParams<{ goal_id: string }>();
	const resolvedGoalId = goalId ?? goalIdFromParams ?? "";
	const [message, setMessage] = useState("");

	const {
		queryParams: { modal },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: resolvedGoalId });
	const goal = data ? data.goal : ({} as Goal);
	const { data: permissions } = useRbacObjectPermissions("goal", resolvedGoalId);
	const { userCanEditStatus } = parseGoalPermissionsForUser(permissions);

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: resolvedGoalId,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: resolvedGoalId,
		sortDirection: "desc",
		sortName: "_id",
	});

	const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : modal === "complete";

	const closeModal = (): void => {
		if (onClose) {
			onClose();
			return;
		}
		handleQueryChange({ modal: undefined });
	};

	const { mutate, isPending } = useMutateGoalDetails(resolvedGoalId, {
		onSuccess: () => {
			refetchGoal();
			refetchProgress();
			refetchActivity();
			onSuccess?.();
			closeModal();
		},
	});

	// Reset message when modal opens
	useEffect(() => {
		if (isOpen) {
			setMessage("");
		}
	}, [isOpen]);

	const handleComplete = (): void => {
		if (!resolvedGoalId) {
			return;
		}

		const updatedData: Array<{ field: string; value: string }> = [{ field: "status", value: "done" }];

		if (message) {
			updatedData.push({ field: "message", value: message });
		}

		mutate(updatedData);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={closeModal} dangerouslySetClassName="w-[432px]">
			{/* Header: centered title + subtitle */}
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Mark as Complete?
				</Text>
				<Text size="paragraph-2" weight="regular">
					{goal?.name}
				</Text>
			</div>

			{/* Content */}
			<div className="px-6 pb-6">
				<div className="flex flex-col gap-2">
					<Text size="paragraph-3" weight="semi-bold">
						Message
					</Text>
					<TextEditor
						id="complete-goal-message"
						initialValue={message}
						placeholder="Add Message"
						minHeight={68}
						maxHeight={100}
						onChange={setMessage}
					/>
				</div>
			</div>

			{/* Footer: centered buttons */}
			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={closeModal}
						data-testid="cancel-complete-goal"
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-[140px]">
					<ButtonLoading
						color="primary"
						size="large"
						disabled={!userCanEditStatus || !resolvedGoalId}
						loading={isPending}
						onClick={handleComplete}
						data-testid="complete-goal"
					>
						Yes, Complete
					</ButtonLoading>
				</div>
			</div>
		</ModalBase>
	);
};

export default CompleteGoalModal;
